<script setup lang="ts">
/**
 * Единая «строка-карточка» корзины.
 * Используется для адресной карточки, времени предзаказа, и т.п. —
 * всё в одном визуальном стиле (rounded-2xl, p-4, border, hover).
 *
 * Структура:
 *   [icon] [title + subtitle] [chevron / custom right]
 *
 * Через слоты:
 *   #icon  — иконка слева (40×40 круг)
 *   default — title (крупный) + subtitle (мелкий)
 *   #right — что справа (по умолчанию chevron если clickable)
 */

interface Props {
  /** Делает карточку кликабельной (cursor + hover), эмитит click */
  clickable?: boolean
  /** Variant: 'default' (нейтральная) | 'error' (красная подсветка для невалидного состояния) */
  variant?: 'default' | 'error'
  /** Заголовок (если не задан default-слот) */
  title?: string
  /** Подзаголовок */
  subtitle?: string
}

withDefaults(defineProps<Props>(), {
  clickable: false,
  variant: 'default',
  title: '',
  subtitle: '',
})

defineEmits<{
  (e: 'click'): void
}>()
</script>

<template>
  <component
    :is="clickable ? 'button' : 'div'"
    :type="clickable ? 'button' : undefined"
    class="basket-row"
    :class="[
      `basket-row--${variant}`,
      { 'basket-row--clickable': clickable },
    ]"
    @click="clickable && $emit('click')"
  >
    <span v-if="$slots.icon" class="basket-row__icon">
      <slot name="icon" />
    </span>

    <span class="basket-row__content">
      <slot>
        <span v-if="title" class="basket-row__title">{{ title }}</span>
        <span v-if="subtitle" class="basket-row__subtitle">{{ subtitle }}</span>
      </slot>
    </span>

    <span class="basket-row__right">
      <slot name="right">
        <UIcon
          v-if="clickable"
          name="i-heroicons-chevron-right"
          class="basket-row__chevron"
        />
      </slot>
    </span>
  </component>
</template>

<style scoped lang="scss">
.basket-row {
  @apply w-full flex items-center gap-3 p-2 text-left;
  border-radius: var(--app-radius-card, 24px);
  @apply bg-hui-content1;
  @apply border border-hui-divider;
  @apply transition-colors duration-200;

  &--clickable {
    @apply cursor-pointer;
    &:hover {
      @apply bg-hui-content2;
    }
    &:active {
      @apply scale-[0.99];
    }
  }

  &--error {
    @apply border-hui-danger;
  }

  &__icon {
    @apply flex items-center justify-center flex-shrink-0;
    @apply w-10 h-10 rounded-full;
    @apply bg-hui-default-100;
    @apply text-hui-default-600;
  }

  &__content {
    @apply flex flex-col flex-1 min-w-0;
  }

  &__title {
    @apply text-base font-semibold text-hui-foreground truncate;
  }

  &__subtitle {
    @apply text-sm text-hui-foreground/50 truncate;
  }

  // Те же стили доступны слот-контенту через :slotted (родитель кладёт классы внутрь default-слота)
  :slotted(.basket-row__title) {
    @apply text-base font-semibold text-hui-foreground truncate block;
  }
  :slotted(.basket-row__subtitle) {
    @apply text-sm text-hui-foreground/50 truncate block;
  }

  &__right {
    @apply flex items-center flex-shrink-0;
  }

  &__chevron {
    @apply text-hui-foreground/40 w-5 h-5;
  }
}
</style>
